import React from "react";
import { Bot, Globe, Database, Server } from "lucide-react";
import { TagList } from "../../../../components/shared/TagSystem/TagList/TagList";
import { MetricsGrid } from "../../../../components/shared/MetricDisplay/MetricsGrid/MetricsGrid";
import { cn } from "../../../../lib/utils";
import { getThemeClass } from "../../../../lib/theme";
import {
  Section,
  SectionContainer,
  SectionHeader,
} from "../../../../components/shared/Section";

interface Innovation {
  title: string;
  metrics: {
    value: string;
    label: string;
    description: string;
  }[];
  businessContext?: string[];
  challenges: string[];
  solutions: string[];
  impact: string[];
}

interface DomainInfo {
  id: string;
  icon: React.ReactNode;
  title: string;
  period: string;
  description: string;
  expertise: string[];
  innovations: Innovation[];
  technologies: string[];
}

const domains: DomainInfo[] = [
  {
    id: "edge-computing",
    icon: <Globe className="w-8 h-8 text-sky-600" />,
    title: "Edge Computing & Content Delivery",
    period: "2017 - Present",
    description:
      "Architected global CDN infrastructure serving 292,600+ enterprise sites across 226+ countries, enabling Fortune 500 digital transformations through edge computing and performance optimization innovations",
    expertise: [
      "CDN Architecture & Optimization",
      "Edge Computing Platform Design",
      "Global Performance Testing",
      "Cache Strategy Development",
    ],
    innovations: [
      {
        title: "Global Infrastructure Architecture & Transformation",
        metrics: [
          {
            value: "80%",
            label: "Bandwidth Reduction",
            description: "While maintaining image quality worldwide",
          },
          {
            value: "292,600+",
            label: "Active Sites",
            description: "Serving across 226+ countries and regions",
          },
          {
            value: "10B+",
            label: "Edge Requests",
            description: "Processed biweekly with sub-50ms response times",
          },
          {
            value: "13x",
            label: "Load Capacity",
            description: "Production load validation for global scale",
          },
        ],
        businessContext: [
          "Architecture enables Fortune 500 digital transformations",
          "Infrastructure supports financial services, healthcare, and manufacturing sectors",
          "Platform facilitates multi-billion dollar enterprise operations",
        ],
        challenges: [
          "Scale infrastructure for 292,600+ sites across 226+ countries",
          "Maintain sub-50ms performance across global traffic patterns",
          "Optimize bandwidth by 80% while preserving quality standards",
          "Handle 10+ billion edge requests biweekly with 99.9% availability",
        ],
        solutions: [
          "Architected complete multi-CDN framework (Cloudflare, Akamai)",
          "Designed modular edge computing platform with service bindings",
          "Implemented intelligent request routing and worker orchestration",
          "Created efficient distributed caching with local LRU systems",
          "Developed automated deployment pipelines and testing infrastructure",
        ],
        impact: [
          "Enabled documented enterprise transformations across industries",
          "Delivered $2.14M+ annual cost savings through CDN migration",
          "Achieved 40-60% faster mobile page loads globally",
          "Reduced infrastructure costs while improving performance",
          "Created reusable patterns adopted across enterprise implementations",
        ],
      },
    ],
    technologies: [
      "Cloudflare",
      "Edge Workers",
      "Akamai",
      "AWS CloudFront",
      "Redis",
      "Node.js",
    ],
  },
  {
    id: "distributed-systems",
    icon: <Database className="w-8 h-8 text-sky-600" />,
    title: "Distributed Systems",
    period: "2015 - 2017",
    description:
      "Led distributed systems architecture serving 1,000+ global enterprise customers including Disney, ESPN, Turner, and Sky Sports, transforming video supply chains for world's largest media companies",
    expertise: [
      "Event Processing Architecture",
      "Search System Design",
      "Data Pipeline Optimization",
      "System Performance Tuning",
    ],
    innovations: [
      {
        title: "Event Processing Revolution",
        metrics: [
          {
            value: "5ms",
            label: "Indexing Speed",
            description: "Down from 15 minutes",
          },
          {
            value: "85%",
            label: "API Performance",
            description: "Latency improvement",
          },
          {
            value: "200K+",
            label: "Cost Savings",
            description: "Annual infrastructure savings",
          },
        ],
        challenges: [
          "High indexing latency in existing system",
          "Complex data processing requirements",
          "Infrastructure cost optimization",
        ],
        solutions: [
          "Designed new event bus architecture",
          "Implemented streaming database changes",
          "Optimized search infrastructure",
        ],
        impact: [
          "Dramatically reduced processing time",
          "Significant cost savings",
          "Improved system reliability",
        ],
      },
    ],
    technologies: [
      "Apache Kafka",
      "ElasticSearch",
      "AWS Lambda",
      "PostgreSQL",
      "Redis",
    ],
  },
  {
    id: "platform-architecture",
    icon: <Server className="w-8 h-8 text-sky-600" />,
    title: "Platform Architecture",
    period: "2014 - 2015",
    description: "Led the architectural design and implementation of comprehensive subscription and payment platforms, enabling digital transformation for major educational content providers.",
    expertise: [
      "Subscription System Architecture",
      "Payment Gateway Integration",
      "Multi-Platform Development",
      "Billing & Revenue Systems",
      "Tax Compliance & Reporting"
    ],
    innovations: [
      {
        title: "End-to-End Subscription Platform",
        metrics: [
          {
            value: "Full-Stack",
            label: "Architecture",
            description: "Complete subscription lifecycle management"
          },
          {
            value: "6+",
            label: "Payment Methods",
            description: "Major payment providers integrated like Stripe, AmazonPay, PayPal"
          },
          {
            value: "4+",
            label: "Platforms",
            description: "Web, mobile, smart TV & OTT"
          }
        ],
        challenges: [
          "Complex subscription lifecycle management",
          "Multi-platform payment processing",
          "Chargeback handling and dispute resolution",
          "Payment reconciliation across providers",
          "Tax calculation and filing compliance",
          "Complex billing scenarios (pro-ration, refunds)",
          "Cross-platform subscription state management"
        ],
        solutions: [
          "Designed reliable webhook-based payment processing",
          "Implemented comprehensive reconciliation system",
          "Integrated Avalara for automated tax compliance",
          "Built sophisticated billing management system",
          "Created unified subscription state machine"
        ],
        impact: [
          "Powered The Great Courses Plus transition to digital subscription model",
          "Enabled automated financial reporting and reconciliation at scale",
          "Supported multiple subscription tiers with sophisticated pro-ration logic",
          "Integrated tax compliance across multiple jurisdictions via Avalara",
          "Platform facilitated business model transformation from physical to digital",
          "Created foundation for digital-first content delivery strategy"
        ]
      }
    ],
    technologies: [
      "Java/Spring",
      "Payment Gateway APIs",
      "Webhook Integrations",
      "Avalara Tax API",
      "RESTful Services",
      "iOS/Android SDKs"
    ]
  },
  {
    id: "ai-platforms",
    icon: <Bot className="w-8 h-8 text-sky-600" />,
    title: "AI Platforms & Agentic Systems",
    period: "2024 - Present",
    description:
      "Building AI platforms for metadata discovery, quality evaluation, direct-authored page creation, and edge-native agent systems.",
    expertise: [
      "Multi-LLM Platform Architecture",
      "Metadata Intelligence",
      "LLM Quality Evaluation",
      "Agentic Systems",
      "Direct-Authored AI Experiences",
    ],
    innovations: [
      {
        title: "Metadata Intelligence, Quick Pages & Agentic CDN Research",
        metrics: [
          {
            value: "26,000+",
            label: "Standard Components",
            description: "Metadata discovery and quality analysis",
          },
          {
            value: "~1M",
            label: "Custom Components",
            description: "Supported by the same enrichment platform",
          },
          {
            value: "3",
            label: "LLM Providers",
            description: "GPT, Claude, and Gemini",
          },
        ],
        businessContext: [
          "Metadata Intelligence was recognized by VP-level leadership as foundational to Salesforce's Agentic AI direction",
          "Quick Pages is in open beta through the end of October 2026",
          "Quick Pages was publicly unveiled at the Dreamforce 2026 Admin Keynote on September 17 and a dedicated Theater 4 session on September 16",
          "Public coverage includes Salesforce Admins, Salesforce Ben, and Concretio",
        ],
        challenges: [
          "Apply consistent discovery and quality analysis across 26,000+ standard and approximately one million custom Salesforce components",
          "Evaluate outputs across multiple LLM providers while tolerating asynchronous failures",
          "Build page creation around direct LLM authorship without adding an agent layer",
          "Connect edge-native AI research with systems that operate under production constraints",
        ],
        solutions: [
          "Built a multi-LLM enrichment platform integrating GPT, Claude, and Gemini",
          "Added LLM-as-a-Judge evaluation and asynchronous fault-tolerant processing",
          "Co-built Quick Pages as part of a six-person team, including joint work on its no-agent LLM approach",
          "Published Agentic CDN research with collaborators working across edge inference and control",
        ],
        impact: [
          "Established a common platform for metadata discovery and quality analysis across standard and custom components",
          "Created a direct-authored page-building experience now being evaluated in open beta",
          "Brought Quick Pages to public Dreamforce 2026 sessions through shared product, engineering, and leadership work",
          "Connected academic research on agentic CDNs with current edge and AI platform architecture",
        ],
      },
    ],
    technologies: [
      "GPT",
      "Claude",
      "Gemini",
      "LLM-as-a-Judge",
      "Asynchronous Processing",
      "Knowledge Graphs",
      "Cloudflare Workers",
    ],
  }
];

const domainOrder = [
  "ai-platforms",
  "edge-computing",
  "distributed-systems",
  "platform-architecture",
];

const orderedDomains = [...domains].sort(
  (a, b) => domainOrder.indexOf(a.id) - domainOrder.indexOf(b.id)
);

const DomainSections: React.FC = () => {
  return (
    <>
      {orderedDomains.map((domain, index) => (
        <Section key={domain.id} id={domain.id} background="light" spacing="sm" className="scroll-mt-16" withDivider={index !== orderedDomains.length - 1} >
          <div className="mb-10">
            {/* Domain Header */}
            <div className="flex items-center mb-6">
              <div
                className={cn(
                  "p-2 rounded-lg",
                  getThemeClass("colors.primary.surface")
                )}
              >
                {domain.icon}
              </div>
              <div className="ml-4">
                <h2
                  className={cn(
                    "text-2xl font-bold",
                    getThemeClass("colors.text.primary")
                  )}
                >
                  {domain.title}
                </h2>
                <p className={getThemeClass("colors.text.secondary")}>
                  {domain.period}
                </p>
              </div>
            </div>

            {/* Domain Overview */}
            <p
                className={cn(
                  "text-lg mb-8",
                  getThemeClass("colors.text.secondary")
                )}
              >
                {domain.description}
              </p>

              {/* Expertise Areas */}
              <div className="mb-8">
                <h3
                  className={cn(
                    "text-lg font-semibold mb-4",
                    getThemeClass("colors.text.primary")
                  )}
                >
                  Areas of Expertise
                </h3>
                <TagList
                  tags={domain.expertise.map((expertise) => ({
                    label: expertise,
                    variant: "default",
                    size: "md",
                  }))}
                  spacing="sm"
                />
              </div>

              {/* Innovations */}
              {domain.innovations.map((innovation, idx) => (
                <div
                  key={idx}
                  className={cn(
                    "rounded-lg shadow-sm p-8 mb-8",
                    getThemeClass("colors.background.surface"),
                    getThemeClass("colors.border.light"),
                    "border",
                    "transition-all duration-300",
                    getThemeClass("colors.border.hover", "hover")
                  )}
                >
                  <h3
                    className={cn(
                      "text-xl font-semibold mb-6",
                      getThemeClass("colors.text.primary")
                    )}
                  >
                    {innovation.title}
                  </h3>

                  {/* Metrics */}
                  <MetricsGrid
                    metrics={innovation.metrics}
                    columns={3}
                    spacing="md"
                  />

                  {/* Business Context - if present */}
                  {innovation.businessContext && innovation.businessContext.length > 0 && (
                    <div
                      className={cn(
                        "rounded-lg p-6 mt-8",
                        "border border-slate-200",
                        "bg-gradient-to-r from-sky-50 to-indigo-50",
                        "transition-all duration-300 hover:border-slate-300"
                      )}
                    >
                      <h4
                        className={cn(
                          "text-lg font-semibold mb-4",
                          getThemeClass("colors.text.primary")
                        )}
                      >
                        Business Context
                      </h4>
                      <ul className="space-y-3">
                        {innovation.businessContext.map((item, j) => (
                          <li key={j} className="flex items-start group">
                            <span
                              className={cn(
                                "w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0",
                                "bg-indigo-500",
                                "transition-transform duration-300 group-hover:scale-125"
                              )}
                            ></span>
                            <span
                              className={cn(
                                getThemeClass("colors.text.secondary"),
                                "transition-colors duration-300",
                                "group-hover:text-slate-900"
                              )}
                            >
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Challenge-Solution-Impact Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
                    {[
                      {
                        title: "Challenges",
                        items: innovation.challenges,
                        color: getThemeClass("colors.primary.main"),
                        bgColor: getThemeClass("colors.primary.surface"),
                        dotColor: "bg-red-500",
                      },
                      {
                        title: "Solutions",
                        items: innovation.solutions,
                        color: getThemeClass("colors.secondary.main"),
                        bgColor: getThemeClass("colors.secondary.surface"),
                        dotColor: "bg-sky-500",
                      },
                      {
                        title: "Impact",
                        items: innovation.impact,
                        color: getThemeClass("colors.success.main"),
                        bgColor: getThemeClass("colors.success.surface"),
                        dotColor: "bg-emerald-500",
                      },
                    ].map((section, i) => (
                      <div
                        key={i}
                        className={cn(
                          "rounded-lg p-6",
                          "border border-slate-200",
                          section.bgColor,
                          "transition-all duration-300 hover:border-slate-300"
                        )}
                      >
                        <h4
                          className={cn(
                            "text-lg font-semibold mb-4",
                            getThemeClass("colors.text.primary")
                          )}
                        >
                          {section.title}
                        </h4>
                        <ul className="space-y-4">
                          {section.items.map((item, j) => (
                            <li key={j} className="flex items-start group">
                              <span
                                className={cn(
                                  "w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0",
                                  section.dotColor,
                                  "transition-transform duration-300 group-hover:scale-125"
                                )}
                              ></span>
                              <span
                                className={cn(
                                  getThemeClass("colors.text.secondary"),
                                  "transition-colors duration-300",
                                  "group-hover:text-slate-900"
                                )}
                              >
                                {item}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>


                </div>
              ))}

              {/* Technologies */}
              <div>
                <h3
                  className={cn(
                    "text-lg font-semibold mb-4",
                    getThemeClass("colors.text.primary")
                  )}
                >
                  Technologies Used
                </h3>
                <TagList
                  tags={domain.technologies.map((tech) => ({
                    label: tech,
                    variant: "primary",
                    size: "md",
                  }))}
                  spacing="sm"
                />
              </div>
          </div>
          
        </Section>
      ))}
    </>
  );
};

export default DomainSections;
